import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type FiscalDocumentType = "NFCE" | "NFSE";
export type FiscalInternalStatus = "PENDING_SEND" | "PROCESSING" | "AUTHORIZED" | "REJECTED" | "CANCELLED" | "ERROR";

export interface FiscalDocumentEvent {
  id: string;
  fiscal_document_id: string;
  event_type: string;
  event_source: string;
  event_payload: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
}

export interface FiscalDocument {
  id: string;
  sale_id: string;
  sale_return_id?: string | null;
  clinic_id: string;
  customer_id: string;
  document_type: FiscalDocumentType;
  reference: string;
  internal_status: FiscalInternalStatus;
  focus_status: string | null;
  fiscal_number: string | null;
  fiscal_series: string | null;
  access_key: string | null;
  print_url: string | null;
  xml_url: string | null;
  request_payload: Record<string, unknown>;
  response_payload: Record<string, unknown>;
  last_error: string | null;
  issued_at: string | null;
  cancelled_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  events?: FiscalDocumentEvent[];
}

export type FiscalSummaryStatus = "NAO_EMITIDA" | "PROCESSANDO" | "EMITIDA" | "ERRO" | "CANCELADA";

export interface FiscalSaleSummary {
  status: FiscalSummaryStatus;
  nfceStatus: FiscalSummaryStatus;
  nfseStatus: FiscalSummaryStatus;
  hasNFCE: boolean;
  hasNFSE: boolean;
  lastUpdatedAt: string | null;
}

export function useSaleFiscalDocuments(saleId: string | null, enabled = true) {
  return useQuery<FiscalDocument[]>({
    queryKey: ["fiscal-documents", saleId],
    enabled: enabled && !!saleId,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fiscal-sale-documents", {
        body: { saleId },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Falha ao carregar documentos fiscais");

      return (data.documents ?? []) as FiscalDocument[];
    },
  });
}

export function useIssueFiscalDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ saleId, documentType }: { saleId: string; documentType: FiscalDocumentType }) => {
      const { data, error } = await supabase.functions.invoke("fiscal-issue-document", {
        body: { saleId, documentType, clientEnvironment: import.meta.env.MODE },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return data as { success: boolean; document: FiscalDocument; focusResponse?: Record<string, unknown> };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-documents", data.document.sale_id] });
      queryClient.invalidateQueries({ queryKey: ["sales-fiscal-summary"] });
      queryClient.invalidateQueries({ queryKey: ["sales-list"] });
      toast.success("Solicitação de emissão fiscal enviada.");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao emitir documento fiscal.");
    },
  });
}

export function useIssueReturnFiscalDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ saleReturnId }: { saleReturnId: string }) => {
      const { data, error } = await supabase.functions.invoke("fiscal-issue-document", {
        body: {
          saleReturnId,
          documentType: "NFCE" as FiscalDocumentType,
          clientEnvironment: import.meta.env.MODE,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return data as { success: boolean; document: FiscalDocument; focusResponse?: Record<string, unknown> };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-documents", data.document.sale_id] });
      queryClient.invalidateQueries({ queryKey: ["sale-returns", data.document.sale_id] });
      queryClient.invalidateQueries({ queryKey: ["sales-fiscal-summary"] });
      queryClient.invalidateQueries({ queryKey: ["sales-list"] });
      toast.success("Solicitação de emissão da nota de devolução enviada.");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao emitir nota de devolução.");
    },
  });
}

export function useSyncFiscalDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId }: { documentId: string }) => {
      const { data, error } = await supabase.functions.invoke("fiscal-sync-document", {
        body: { documentId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return data as { success: boolean; document: FiscalDocument; focusResponse?: Record<string, unknown> };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-documents", data.document.sale_id] });
      queryClient.invalidateQueries({ queryKey: ["sales-fiscal-summary"] });
      queryClient.invalidateQueries({ queryKey: ["sales-list"] });
      toast.success("Status fiscal sincronizado.");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao sincronizar status fiscal.");
    },
  });
}

export function useSalesFiscalSummary(saleIds: string[]) {
  return useQuery<Record<string, FiscalSaleSummary>>({
    queryKey: ["sales-fiscal-summary", saleIds],
    enabled: saleIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fiscal-sales-summary", {
        body: { saleIds },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Erro ao carregar resumo fiscal");

      return (data.summary ?? {}) as Record<string, FiscalSaleSummary>;
    },
  });
}
